import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout/AccountDetailsFlyout';
import ClientDetailsFlyout from '@/components/flyouts/ClientDetailsFlyout/ClientDetailsFlyout';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import type { AccountBase } from '@/models/AccountViewModel';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { useGetAdvisorInvestorsQuery } from '@/store/advisorInvestorsApi';
import { useGetAdvisorInvestorGroupsQuery } from '@/store/clientsApi';
import {
  API_DERIVED_OPPORTUNITY_STATUSES,
  CLEAR_OPPORTUNITY_STATUS,
  OpportunityRuleIdEnum,
  OpportunityStatusEnum,
  SETTABLE_OPPORTUNITY_STATUSES,
  useGetInvestorOpportunitiesQuery,
  useGetRuleOpportunitiesQuery,
  useUpdateOpportunityMutation,
  type OpportunityItem,
  type OpportunityItemsResponse,
  type OpportunityRuleRecord,
} from '@/store/insightsApi';
import {
  buildOpportunityStatusUpdate,
  getStatusText,
  getStatusColor,
  parseStatusId,
} from './insightsUtils';
import type { AdvisorInvestor } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridApiPremium,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import dayjs from '@/config/dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative, {
  type CustomToolbarForNativeProps,
} from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import {
  GridContainer,
  type GridContainerSize,
} from '../../components/grids/GridContainer';

type OpportunitiesGridProps = {
  investorId?: number;
  ruleId?: OpportunityRuleIdEnum;
  startDate?: string; // MM/DD/YYYY
  endDate?: string; // MM/DD/YYYY
  ruleNameFilter?: string; // e.g., "New Accounts" for Money In Motion
  size?: GridContainerSize;
};

type OpportunityRow = {
  id: string;
  clientName?: string | null;
  insightType: string;
  accountName?: string | null;
  amount: number;
  ruleName: string;
  ruleType: string;
  date?: string | null;
  statusId?: OpportunityStatusEnum | null;
  statusName?: string | null;
  wealthAccountId?: number | null;
  opportunityId?: number;
  investorId?: number | null;
  _ruleId?: OpportunityRuleIdEnum; // for updates
  _raw?: OpportunityItem;
  description?: string | null;
};

const STATUS_SELECT_MIN_WIDTH = 150;

const StatusDot = ({ status }: { status: OpportunityStatusEnum | null }) => (
  <Box sx={{ width: 8, flexShrink: 0 }}>
    {status != null && (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: getStatusColor(status),
        }}
      />
    )}
  </Box>
);

const StatusCell = ({ row }: { row: OpportunityRow }) => {
  const { t } = useLocalization();
  const [updateOpportunity] = useUpdateOpportunityMutation();

  const current = row.statusId ?? null;

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const handleChange = async (
    e: import('@mui/material/Select').SelectChangeEvent<number>,
  ) => {
    const next = e.target.value;
    if (!row._ruleId || !row.opportunityId || !row._raw) return;

    const body = buildOpportunityStatusUpdate(row._raw, next);

    try {
      await updateOpportunity({
        ruleId: row._ruleId,
        opportunityId: row.opportunityId,
        body,
      }).unwrap();
    } catch {
      setErrorMsg(
        t('opportunityUpdateFailed', 'Failed to update opportunity status'),
      );
      setErrorOpen(true);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <StatusDot status={current} />
      <Select
        value={current ?? ''}
        onChange={(event) => {
          void handleChange(event);
        }}
        displayEmpty
        input={<Input disableUnderline />}
        sx={{
          minWidth: STATUS_SELECT_MIN_WIDTH,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
      >
        {current != null &&
          API_DERIVED_OPPORTUNITY_STATUSES.includes(current) && (
            <MenuItem
              value={current}
              disabled
            >
              {getStatusText(current, t)}
            </MenuItem>
          )}
        {SETTABLE_OPPORTUNITY_STATUSES.map((status) => (
          <MenuItem
            key={status}
            value={status}
          >
            {getStatusText(status, t)}
          </MenuItem>
        ))}
        {current != null && SETTABLE_OPPORTUNITY_STATUSES.includes(current) && (
          <MenuItem value={CLEAR_OPPORTUNITY_STATUS}>
            {t('clearOpportunityStatus', 'Clear status')}
          </MenuItem>
        )}
      </Select>

      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => {
          setErrorOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setErrorOpen(false);
          }}
          severity='error'
          sx={{ width: '100%' }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default function OpportunitiesGrid({
  investorId,
  ruleId,
  startDate,
  endDate,
  ruleNameFilter,
  size,
}: OpportunitiesGridProps) {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();
  const isNative = Capacitor.getPlatform() !== 'web';
  const defaultStart = useMemo(
    () =>
      startDate ? startDate : dayjs().subtract(1, 'year').format('M/D/YYYY'),
    [startDate],
  );
  const defaultEnd = useMemo(
    () => (endDate ? endDate : dayjs().format('M/D/YYYY')),
    [endDate],
  );

  // --- Client and Client Group filters ---
  const [selectedClientId, setSelectedClientId] = useState<number | '' | null>(
    '',
  );
  const [selectedGroupId, setSelectedGroupId] = useState<number | '' | null>(
    '',
  );

  const {
    data: oppData,
    isLoading: oppLoading,
    isFetching: oppFetching,
  } = useGetInvestorOpportunitiesQuery(investorId as number, {
    skip: !authLoaded || !investorId || ruleId != null,
  });

  // Load opportunities by rule(s)
  const { data: singleRuleData, isFetching: singleRuleFetching } =
    useGetRuleOpportunitiesQuery(
      {
        ruleId: ruleId ?? OpportunityRuleIdEnum.MoneyInMotionNewAccounts,
        startDate: defaultStart,
        endDate: defaultEnd,
        groupId:
          typeof selectedGroupId === 'number' ? selectedGroupId : undefined,
        ...(typeof investorId === 'number' ? { investorId } : {}),
      },
      { skip: !authLoaded || ruleId == null },
    );
  const { data: mimData, isFetching: mimFetching } =
    useGetRuleOpportunitiesQuery(
      {
        ruleId: OpportunityRuleIdEnum.MoneyInMotionNewAccounts,
        startDate: defaultStart,
        endDate: defaultEnd,
        groupId:
          typeof selectedGroupId === 'number' ? selectedGroupId : undefined,
        ...(typeof investorId === 'number' ? { investorId } : {}),
      },
      { skip: !authLoaded || ruleId != null },
    );
  const { data: mimLargeData, isFetching: mimLargeFetching } =
    useGetRuleOpportunitiesQuery(
      {
        ruleId: OpportunityRuleIdEnum.MoneyInMotionLargeDeposits,
        startDate: defaultStart,
        endDate: defaultEnd,
        groupId:
          typeof selectedGroupId === 'number' ? selectedGroupId : undefined,
        ...(typeof investorId === 'number' ? { investorId } : {}),
      },
      { skip: !authLoaded || ruleId != null },
    );
  const { data: rolData, isFetching: rolFetching } =
    useGetRuleOpportunitiesQuery(
      {
        ruleId: OpportunityRuleIdEnum.Rollover,
        startDate: defaultStart,
        endDate: defaultEnd,
        groupId:
          typeof selectedGroupId === 'number' ? selectedGroupId : undefined,
        ...(typeof investorId === 'number' ? { investorId } : {}),
      },
      { skip: !authLoaded || ruleId != null },
    );
  const { data: cashData, isFetching: cashFetching } =
    useGetRuleOpportunitiesQuery(
      {
        ruleId: OpportunityRuleIdEnum.InvestableCash,
        startDate: defaultStart,
        endDate: defaultEnd,
        groupId:
          typeof selectedGroupId === 'number' ? selectedGroupId : undefined,
        ...(typeof investorId === 'number' ? { investorId } : {}),
      },
      { skip: !authLoaded || ruleId != null },
    );

  const shouldLoadAccounts = authLoaded && investorId != null;
  const { data: accountsData } = useGetAccountsQuery(
    { showClosed: false, InvestorId: investorId as number },
    { skip: !shouldLoadAccounts },
  );

  // On-demand fetch of accounts for a selected client (when investorId is not provided)
  const [pendingAccount, setPendingAccount] = useState<{
    wealthAccountId: number;
    investorId: number;
  } | null>(null);
  const { data: pendingInvestorAccounts } = useGetAccountsQuery(
    pendingAccount
      ? { showClosed: false, InvestorId: pendingAccount.investorId }
      : { showClosed: false },
    { skip: !pendingAccount },
  );
  useEffect(() => {
    if (!pendingAccount || !pendingInvestorAccounts?.Records) return;
    const match = pendingInvestorAccounts.Records.find(
      (a) => a.AccountId === pendingAccount.wealthAccountId,
    );
    if (match) {
      setSelectedAccountInvestorId(pendingAccount.investorId);
      setSelectedAccount(match);
      setPendingAccount(null);
    }
  }, [pendingAccount, pendingInvestorAccounts]);

  const opportunityRows: OpportunityRow[] = useMemo(() => {
    const records = oppData?.Records || [];
    const rows: OpportunityRow[] = [];

    records.forEach((type) => {
      type.Rules.forEach((rule: OpportunityRuleRecord) => {
        const amount = Math.max(rule.ExistingAmount, rule.ConversionAmount);
        rows.push({
          id: `${String(type.OpportunityTypeId)}_${String(rule.OppRuleId)}`,
          insightType: type.Name,
          accountName: rule.RuleTypeDisplay === 'Account' ? rule.Name : null,
          amount,
          ruleName: rule.Name,
          ruleType: rule.RuleTypeDisplay,
          wealthAccountId: null,
          description: null,
        });
      });
    });
    return rows.filter((r) => r.amount > 0 || r.ruleType !== 'Transaction');
  }, [oppData]);

  const detailedOpps = useMemo(() => {
    const filterByInvestor = (items: OpportunityItem[]) =>
      investorId
        ? items.filter((r) =>
            (r.Investors || []).some((i) => i.InvestorId === investorId),
          )
        : items;

    const filterByRuleName = (items: OpportunityItem[]) =>
      ruleNameFilter
        ? items.filter(
            (r) =>
              (r.RuleName || '').toLowerCase() === ruleNameFilter.toLowerCase(),
          )
        : items;

    if (ruleId != null) {
      const all = singleRuleData?.Records || [];
      return filterByInvestor(filterByRuleName(all)).map((r) => ({
        item: r,
        ruleId,
      }));
    }

    const result: { item: OpportunityItem; ruleId: OpportunityRuleIdEnum }[] =
      [];
    const pushAll = (
      resp: OpportunityItemsResponse | undefined,
      id: OpportunityRuleIdEnum,
    ) => {
      const recs = resp?.Records || [];
      filterByInvestor(filterByRuleName(recs)).forEach((r) =>
        result.push({ item: r, ruleId: id }),
      );
    };
    pushAll(mimData, OpportunityRuleIdEnum.MoneyInMotionNewAccounts);
    pushAll(mimLargeData, OpportunityRuleIdEnum.MoneyInMotionLargeDeposits);
    pushAll(rolData, OpportunityRuleIdEnum.Rollover);
    pushAll(cashData, OpportunityRuleIdEnum.InvestableCash);
    return result;
  }, [
    singleRuleData,
    mimData,
    mimLargeData,
    rolData,
    cashData,
    investorId,
    ruleId,
    ruleNameFilter,
  ]);

  const detailedRows: OpportunityRow[] = useMemo(() => {
    return detailedOpps.map(({ item, ruleId: rid }) => ({
      id: String(item.OpportunityId),
      clientName: item.Investors?.[0]?.InvestorName || null,
      insightType: item.TypeName,
      accountName: item.WealthAccountName || null,
      amount: item.Amount,
      ruleName: item.RuleName,
      ruleType: 'Account',
      date: item.Date,
      statusId: item.StatusId,
      statusName: getStatusText(item.StatusId, t),
      wealthAccountId: item.WealthAccountId,
      opportunityId: item.OpportunityId,
      investorId: item.Investors?.[0]?.InvestorId || null,
      _ruleId: rid,
      _raw: item,
      description: item.Description,
    }));
  }, [detailedOpps, t]);

  // Load list of client groups for dropdown
  const { data: investorGroupsData } = useGetAdvisorInvestorGroupsQuery();
  // Load investors within selected group to build membership set
  const { data: groupInvestorsData } = useGetAdvisorInvestorsQuery(
    selectedGroupId && typeof selectedGroupId === 'number'
      ? { groupId: selectedGroupId }
      : {},
    { skip: !(selectedGroupId && typeof selectedGroupId === 'number') },
  );

  const apiRef = useGridApiRef();
  const [rowGroupingModel, setRowGroupingModel] = useState<string[]>([
    'statusId',
  ]);
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    rowGroupingModel,
    initialState: {
      columns: {
        columnVisibilityModel: {
          // Hide the column used for leaves
          statusId: false,
        },
      },
      rowGrouping: { model: ['statusId'] },
    },
  });
  const groupingEnabled = rowGroupingModel.length > 0;

  const clientOptions = useMemo(() => {
    const map = new Map<number, string>();
    detailedRows.forEach((r) => {
      if (typeof r.investorId === 'number' && r.clientName) {
        map.set(r.investorId, r.clientName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [detailedRows]);

  const groupOptions = useMemo(() => {
    const groups = (investorGroupsData?.Records || []) as Array<{
      AdvisorNamedInvestorGroupId: number;
      Name: string;
    }>;
    return groups.map((g) => ({
      id: g.AdvisorNamedInvestorGroupId,
      name: g.Name,
    }));
  }, [investorGroupsData]);

  const groupInvestorIdSet = useMemo(() => {
    const ids = new Set<number>();
    const recs = groupInvestorsData?.Records || [];
    recs.forEach((inv) => {
      if (typeof inv.InvestorId === 'number') ids.add(inv.InvestorId);
    });
    return ids;
  }, [groupInvestorsData]);

  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );
  const [selectedAccountInvestorId, setSelectedAccountInvestorId] = useState<
    number | undefined
  >(undefined);
  const [selectedClient, setSelectedClient] = useState<AdvisorInvestor | null>(
    null,
  );

  const columns: GridColDef<OpportunityRow>[] = useMemo(() => {
    const isLargeDepositsView =
      ruleId === OpportunityRuleIdEnum.MoneyInMotionLargeDeposits ||
      (typeof ruleNameFilter === 'string' &&
        ruleNameFilter.toLowerCase() === 'large deposits'.toLowerCase());

    return [
      ...(investorId
        ? []
        : [
            {
              field: 'clientName',
              headerName: t('client', 'Client'),
              flex: 1,
              minWidth: 175,
              renderCell: (params: GridRenderCellParams<OpportunityRow>) => {
                // When the row is a group, render the grouping cell content
                const node = params.rowNode;
                if (node.type === 'group') {
                  const rawValue = node.groupingKey;
                  const statusValue = parseStatusId(rawValue);
                  const label = getStatusText(statusValue, t);
                  const handleToggle = (event: React.MouseEvent) => {
                    event.stopPropagation();
                    event.preventDefault();
                    (params.api as GridApiPremium).setRowChildrenExpansion(
                      params.id,
                      !node.childrenExpanded,
                    );
                  };
                  const indentation = (node.depth || 0) * 2;
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: '100%',
                        pl: indentation,
                      }}
                    >
                      <IconButton
                        size='small'
                        onClick={handleToggle}
                        aria-label={label || undefined}
                        sx={{ p: 0.5 }}
                      >
                        <KeyboardArrowRight
                          fontSize='small'
                          sx={{
                            transform: node.childrenExpanded
                              ? 'rotate(90deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                          }}
                        />
                      </IconButton>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <StatusDot status={statusValue} />
                        <Typography variant='body2'>{label}</Typography>
                      </Box>
                    </Box>
                  );
                }

                // Otherwise render the usual client cell content
                const row = params.row;
                const label = row.clientName || '';
                const clickable = !!row.investorId;
                const onClick = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (!row.investorId) return;
                  const clientStub: AdvisorInvestor = {
                    CanMGPSSO: false,
                    InvestorId: row.investorId,
                    Name: label,
                    NetWorth: 0,
                    TotalAssets: 0,
                    TotalLiabilities: 0,
                    Home: 0,
                    HomePercentage: 0,
                    OnboardingStep: 0,
                    OnboardingStepDisplay: '',
                    CanInvite: false,
                    Groups: [],
                    CanImpersonate: false,
                    CanDelete: false,
                    InHousehold: false,
                    UserId: 0,
                    UserName: '',
                    IsAdminLocked: false,
                    IsSensitiveUser: false,
                  };
                  setSelectedClient(clientStub);
                };

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {clickable ? (
                      <Typography
                        variant='body2'
                        color='primary'
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={onClick}
                      >
                        {label}
                      </Typography>
                    ) : (
                      <Typography variant='body2'>{label}</Typography>
                    )}
                  </Box>
                );
              },
            } as GridColDef<OpportunityRow>,
          ]),
      {
        field: 'insightType',
        headerName: t('insightType', 'Insight Type'),
        flex: 1,
        minWidth: 220,
        hide: !!ruleId,
        renderCell: (params: GridRenderCellParams<OpportunityRow>) => {
          const node = params.rowNode;
          if (node.type === 'group') {
            if (!investorId) {
              return null;
            }
            const rawValue = node.groupingKey;
            const statusValue = parseStatusId(rawValue);
            const label = getStatusText(statusValue, t);
            const handleToggle = (event: React.MouseEvent) => {
              event.stopPropagation();
              event.preventDefault();
              (params.api as GridApiPremium).setRowChildrenExpansion(
                params.id,
                !node.childrenExpanded,
              );
            };
            const indentation = (node.depth || 0) * 2;
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: '100%',
                  pl: indentation,
                }}
              >
                <IconButton
                  size='small'
                  onClick={handleToggle}
                  aria-label={label || undefined}
                  sx={{ p: 0.5 }}
                >
                  <KeyboardArrowRight
                    fontSize='small'
                    sx={{
                      transform: node.childrenExpanded
                        ? 'rotate(90deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusDot status={statusValue} />
                  <Typography variant='body2'>{label}</Typography>
                </Box>
              </Box>
            );
          }
          const value = params.value ? String(params.value) : '';
          return <Typography variant='body2'>{value}</Typography>;
        },
      },
      {
        field: 'accountName',
        headerName: t('accountName', 'Account Name'),
        flex: 1,
        minWidth: 175,
        renderCell: (params) => {
          const label = params.value ? String(params.value) : '';
          const wealthAccountId = params.row.wealthAccountId;

          if (!wealthAccountId) {
            return <Typography variant='body2'>{label}</Typography>;
          }

          const targetAccount = accountsData?.Records.find(
            (a: AccountBase) => a.AccountId === wealthAccountId,
          );

          const row = params.row;
          const onClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (targetAccount) {
              setSelectedAccountInvestorId(
                typeof investorId === 'number'
                  ? investorId
                  : (row.investorId ?? undefined),
              );
              setSelectedAccount(targetAccount);
            } else if (row.investorId && wealthAccountId) {
              setPendingAccount({
                wealthAccountId,
                investorId: row.investorId,
              });
            }
          };

          const clickable = !!(
            targetAccount ||
            (row.investorId && wealthAccountId)
          );
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {clickable ? (
                <Typography
                  variant='body2'
                  color='primary'
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={onClick}
                >
                  {label}
                </Typography>
              ) : (
                <Typography variant='body2'>{label}</Typography>
              )}
            </Box>
          );
        },
      },
      ...(isLargeDepositsView
        ? [
            {
              field: 'description',
              headerName: t('description', 'Description'),
              // flex: 1.2,
              // minWidth: 140,
            } as GridColDef<OpportunityRow>,
          ]
        : []),
      {
        field: 'amount',
        headerName: isLargeDepositsView
          ? t('transactionAmount', 'Transaction Amount')
          : t('amount', 'Amount'),
        minWidth: 140,
        flex: 0.5,
        valueFormatter: (_value, row) => formatNumberToCurrency(row.amount),
        renderCell: (params: GridRenderCellParams<OpportunityRow>) => {
          const node = params.rowNode;
          if (node.type !== 'leaf') {
            return null;
          }
          return formatNumberToCurrency(params.row.amount);
        },
      },
      {
        field: 'date',
        headerName: isLargeDepositsView
          ? t('transactionDate', 'Transaction Date')
          : t('dateCreated', 'Date Created'),
        minWidth: 140,
        flex: 0.5,
        valueFormatter: (_value, row) =>
          row.date ? dayjs(row.date).format('M/D/YY') : '',
      },
      {
        field: 'statusId',
        headerName: t('status', 'Status'),
        minWidth: 180,
        flex: 0.8,
        valueFormatter: (_value, row) => getStatusText(row.statusId, t),
        renderCell: (params: GridRenderCellParams<OpportunityRow>) => {
          const node = params.rowNode;
          if (node.type === 'group') {
            return null;
          }
          return <StatusCell row={params.row} />;
        },
      },
      {
        field: 'ruleName',
        headerName: t('rule', 'Rule'),
        flex: 1.2,
        minWidth: 240,
        hide: true,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
      },
    ];
  }, [t, accountsData, investorId, ruleId, ruleNameFilter]);

  const isLoading =
    oppLoading ||
    oppFetching ||
    singleRuleFetching ||
    mimFetching ||
    mimLargeFetching ||
    rolFetching ||
    cashFetching ||
    !authLoaded;

  // Apply client/group filters to the data shown in the grid (detailed rows only)
  const rowsForGrid = useMemo(() => {
    const base = detailedRows.length > 0 ? detailedRows : opportunityRows;
    if (base !== detailedRows) return base; // Only filter detailed lists

    return detailedRows.filter((r) => {
      let ok = true;
      if (typeof selectedClientId === 'number') {
        ok = r.investorId === selectedClientId;
      }
      if (ok && typeof selectedGroupId === 'number') {
        ok = r.investorId != null && groupInvestorIdSet.has(r.investorId);
      }
      return ok;
    });
  }, [
    detailedRows,
    opportunityRows,
    selectedClientId,
    selectedGroupId,
    groupInvestorIdSet,
  ]);

  return (
    <Box>
      <Typography
        variant='subtitle1'
        sx={{ mb: 1 }}
      >
        {t('opportunities', 'Opportunities')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={groupingEnabled}
              onChange={(_, checked) => {
                setRowGroupingModel(checked ? ['statusId'] : []);
              }}
            />
          }
          label={t('groupByStatus', 'Group by Status')}
        />
      </Box>
      {detailedRows.length > 0 && investorId == null && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
          <FormControl
            size='small'
            sx={{ minWidth: 220 }}
          >
            <InputLabel id='client-select-label'>
              {t('client', 'Client')}
            </InputLabel>
            <Select
              labelId='client-select-label'
              id='client-select'
              label={t('client', 'Client')}
              value={selectedClientId ?? ''}
              onChange={(e) => {
                setSelectedClientId(e.target.value);
              }}
            >
              <MenuItem value=''>
                {t('selectClient', 'Select a Client')}
              </MenuItem>
              {clientOptions.map((opt) => (
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                >
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('or', 'Or')}
          </Typography>
          <FormControl
            size='small'
            sx={{ minWidth: 240 }}
          >
            <InputLabel id='client-group-select-label'>
              {t('clientGroup', 'Client Group')}
            </InputLabel>
            <Select
              labelId='client-group-select-label'
              id='client-group-select'
              label={t('clientGroup', 'Client Group')}
              value={selectedGroupId ?? ''}
              onChange={(e) => {
                setSelectedGroupId(e.target.value);
              }}
            >
              <MenuItem value=''>
                {t('selectClientGroup', 'Select a Client Group')}
              </MenuItem>
              {groupOptions.map((opt) => (
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                >
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <GridContainer
        isWidget={false}
        size={size}
      >
        <DataGridPremium
          disableRowSelectionOnClick
          showToolbar
          disablePivoting
          apiRef={apiRef}
          initialState={initialState}
          rowGroupingModel={rowGroupingModel}
          onRowGroupingModelChange={(model) => {
            setRowGroupingModel(Array.isArray(model) ? model : []);
          }}
          groupingColDef={{
            valueFormatter: (value) => getStatusText(parseStatusId(value), t),
          }}
          isGroupExpandedByDefault={(node) => {
            if (node.groupingField !== 'statusId') {
              return true;
            }
            const statusValue = parseStatusId(node.groupingKey);
            if (statusValue == null) {
              return true;
            }
            // Collapse the 'Closed' status group by default; expand others.
            return statusValue !== OpportunityStatusEnum.Closed;
          }}
          columnVisibilityModel={{
            ruleName: false,
            ...(ruleId != null ? { insightType: false } : {}),
            ...(groupingEnabled
              ? { [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD]: false }
              : {}),
          }}
          rows={rowsForGrid}
          columns={columns}
          getRowId={(row) => row.id}
          hideFooter
          loading={isLoading}
          slotProps={{
            toolbar: {
              ...defaultToolbarExportSlotProps,
              showQuickFilter: true,
            },
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
          }}
          slots={{
            columnMenu: () => null,
            ...(isNative
              ? {
                  toolbar: (props: CustomToolbarForNativeProps) => (
                    <CustomToolbarForNative {...props} />
                  ),
                }
              : {}),
          }}
        />
      </GridContainer>

      {selectedAccount && (
        <AccountDetailsFlyout
          open={!!selectedAccount}
          toggleDrawer={(v) => {
            if (!v) {
              setSelectedAccount(null);
              setSelectedAccountInvestorId(undefined);
            }
          }}
          account={selectedAccount}
          investorId={selectedAccountInvestorId}
        />
      )}
      {selectedClient && (
        <ClientDetailsFlyout
          open={!!selectedClient}
          toggleDrawer={(open) => {
            if (!open) {
              setSelectedClient(null);
            }
          }}
          client={selectedClient}
        />
      )}
    </Box>
  );
}
